/**
 * The forced identity check sheet.
 *
 * A moderator opens a check on birthday or gender, and the account answers it
 * before it reaches the rest of the site. One field per sheet, birthday first.
 * Each field has two ends: save a different value, or keep the stored one.
 * Both close the check and the lock returns.
 *
 * The sheet is not dismissible, so the component exposes no close action. The
 * server decides when the page goes away, because it renders the page only
 * while a check is open.
 */

import { editorBase } from './alpine-editor-base.js';
import { ageFromIsoDate } from './utils/age.js';

const SELF_ENDPOINT = '/account/self';
const CONFIRM_ENDPOINT = '/account/identity-check';

const FIELD_BIRTHDAY = 'birthday';

// ValidationException::CODE_CHANGE_LIMIT_BIRTHDAY / CODE_CHANGE_LIMIT_SEX. The
// lock rules answer these only when the field is locked, which for a sheet
// that is open means the check closed underneath it.
const LOCKED_CODES = ['change_limit_birthday', 'change_limit_sex'];

const STAGE_EDIT = 'edit';
const STAGE_CONFIRM = 'confirm';
const STAGE_SAVED = 'saved';

export function identityCheckSheet(config = {}) {
    return {
        fields: Array.isArray(config.fields) ? config.fields.map(String) : [],
        stored: {
            birthday: String(config.birthday || ''),
            sex: String(config.sex || ''),
        },
        redirect: String(config.redirect || '/'),
        // The server carries the authority for both bounds. A config without
        // them leaves the save rules as the only check, so each default is the
        // permissive end rather than a bound the sheet invents.
        minAge: Number(config.minAge) || 0,
        maxAge: Number(config.maxAge) || Infinity,
        copy: config.copy || {},

        index: 0,
        stage: STAGE_EDIT,
        draft: '',
        savedValue: '',
        saving: false,
        error: '',

        init() {
            this.draft = this.storedValue;
        },

        get field() {
            return this.fields[this.index] || '';
        },

        isBirthdayField(field) {
            return field === FIELD_BIRTHDAY;
        },

        get isBirthday() {
            return this.isBirthdayField(this.field);
        },

        get storedValue() {
            return this.stored[this.field] || '';
        },

        get changed() {
            return this.draft !== '' && this.draft !== this.storedValue;
        },

        /**
         * True for an age the birthday write path accepts. A date outside the
         * bounds is one BirthdayMinAgeRule or BirthdayValidDateUnderMaxAgeRule
         * refuses, so the sheet must not offer it as an answer.
         */
        withinAgeBounds(age) {
            return age !== null && age >= this.minAge && age <= this.maxAge;
        },

        /**
         * users.sex is nullable and users.birth can hold a zero date, so an
         * account can reach the sheet with nothing to confirm. A stored age
         * outside the write bounds has no correct reading either, and the
         * server refuses to confirm it. Such an account answers the check by
         * typing a value.
         */
        get canConfirm() {
            if (this.storedValue === '') {
                return false;
            }

            return !this.isBirthday || this.withinAgeBounds(ageFromIsoDate(this.storedValue));
        },

        /** The age the typed date gives, shown live under the input. */
        get ageLine() {
            if (!this.isBirthday) {
                return '';
            }

            const age = ageFromIsoDate(this.draft);
            return age === null ? '' : this.fill(this.copy.ageLine, { age });
        },

        get confirmLine() {
            if (this.isBirthday) {
                return this.fill(this.copy.confirmBirthday, { age: ageFromIsoDate(this.draft) });
            }

            return this.fill(this.copy.confirmSex, { gender: this.label(this.draft) });
        },

        /** What the sheet prints in its saved state. */
        get savedLine() {
            if (!this.isBirthday) {
                return this.label(this.savedValue);
            }

            const age = ageFromIsoDate(this.savedValue);
            return age === null ? this.savedValue : `${this.savedValue} (${age})`;
        },

        get hasNext() {
            return this.index + 1 < this.fields.length;
        },

        get nextLabel() {
            const next = this.fields[this.index + 1];
            return this.isBirthdayField(next) ? this.copy.nextBirthday : this.copy.nextSex;
        },

        get heading() {
            return this.isBirthday ? this.copy.birthdayHeading : this.copy.sexHeading;
        },

        label(value) {
            return this.copy[value] || value;
        },

        fill(template, values) {
            let text = String(template || '');
            for (const name of Object.keys(values)) {
                text = text.split(`%${name}%`).join(String(values[name] ?? ''));
            }

            return text;
        },

        askConfirm() {
            if (this.saving || !this.changed) {
                return;
            }

            // The confirm sentence names the age, so the date has to give one
            // the save can keep. A date the helper rejects renders as an empty
            // word, and one outside the write bounds reads as a promise the
            // server then refuses. The sheet cannot be dismissed, so a silent
            // refusal leaves no way forward.
            if (this.isBirthday && !this.withinAgeBounds(ageFromIsoDate(this.draft))) {
                this.error = this.copy.badDate;
                return;
            }

            this.error = '';
            this.stage = STAGE_CONFIRM;
        },

        back() {
            this.stage = STAGE_EDIT;
        },

        /** Save the value the account typed. */
        save() {
            const body = new FormData();
            body.append(this.field, this.draft);

            return this.send(SELF_ENDPOINT, 'PUT', body, this.draft, this.field);
        },

        /** Close the check on the stored value, which stays as it is. */
        keep() {
            const body = new FormData();
            body.append('field', this.field);

            // The confirm endpoint validates one parameter named `field`, so
            // a refusal from it is keyed on that, not on the field name.
            return this.send(CONFIRM_ENDPOINT, 'POST', body, this.storedValue, 'field');
        },

        /**
         * True when the API refused because the field locked again. A moderator
         * can close the check while the account holds the sheet open, either by
         * withdrawing it or by editing the field.
         */
        async lockedAgain(response, errorKey) {
            try {
                const data = await response.clone().json();

                return LOCKED_CODES.includes(data?.error?.errors?.[errorKey]?.code);
            } catch {
                return false;
            }
        },

        async send(endpoint, method, body, resultValue, errorKey) {
            if (this.saving) {
                return;
            }

            this.saving = true;
            this.error = '';

            try {
                // The auth layout mints the token after load. Both sheet actions
                // need it, and ApiClient reads it synchronously.
                if (window.getApiToken) {
                    await window.getApiToken();
                }

                const response = await window.ApiClient.request(endpoint, { method, body });
                if (!response.ok) {
                    // The page renders only while a check is open, so a reload
                    // reaches the gate and leaves. Rendering the refusal instead
                    // would print "you cannot change this" under an input the
                    // sheet still offers.
                    if (await this.lockedAgain(response, errorKey)) {
                        window.location.reload();
                        return;
                    }

                    this.error = await editorBase.handleApiError(response, errorKey, this.copy.failed);
                    this.stage = STAGE_EDIT;
                    return;
                }

                this.stored[this.field] = resultValue;
                this.savedValue = resultValue;
                this.stage = STAGE_SAVED;
            } catch {
                this.error = this.copy.failed;
                this.stage = STAGE_EDIT;
            } finally {
                this.saving = false;
            }
        },

        next() {
            if (!this.hasNext) {
                return;
            }

            this.index += 1;
            this.stage = STAGE_EDIT;
            this.draft = this.storedValue;
            this.savedValue = '';
            this.error = '';
        },

        done() {
            window.location.assign(this.redirect);
        },
    };
}
